import type { BoostId } from '../sim/boosts';
import { loadLevel, type LevelDef } from './levelSchema';
import type { MowerId } from './mowers';
import type { ToolId } from './tools';

/** What a practice lawn shows off: a tool, a boost or a mower's trick. */
export type PracticeItem =
  { kind: 'tool'; id: ToolId } | { kind: 'boost'; id: BoostId } | { kind: 'mower'; id: MowerId };

/**
 * A TRY IT lawn (owner, 2026-10-23: the Garage should show what a tool, boost or trick does): a
 * small lawn made for one item, played free with that item (owned or not), paying and recording
 * nothing. Each is beatable without the item (lawns never require a tool); the item makes it
 * easier or cleaner, which is the point.
 */
export interface PracticeLawn {
  item: PracticeItem;
  /** The mower it is played with: the item's own mower, else the starter Reel Mower. */
  mower: MowerId;
  /** How to use it, for the card before the lawn (one or two short lines). */
  hint: string;
  level: LevelDef;
}

interface Spec {
  item: PracticeItem;
  mower?: MowerId;
  name: string;
  hint: string;
  rows: string[];
  critters?: { kind: 'squirrel'; x: number; y: number }[];
  coins?: [number, number][];
}

const SPECS: readonly Spec[] = [
  // ── Tools ──
  {
    item: { kind: 'tool', id: 'bikeHorn' },
    name: 'Horn Practice',
    hint: 'Tap HORN in the bar below: every critter runs home for a while.',
    rows: [
      '........',
      '........',
      '..h..h..',
      '........',
      '........',
      '..h..h..',
      '........',
      'S.......',
    ],
    critters: [
      { kind: 'squirrel', x: 1, y: 1 },
      { kind: 'squirrel', x: 6, y: 3 },
      { kind: 'squirrel', x: 3, y: 6 },
    ],
  },
  {
    item: { kind: 'tool', id: 'rake' },
    name: 'Rake Practice',
    hint: 'Tap RAKE as you come up to sticks: it clears them out of your way.',
    rows: ['........', '..xxxx..', '........', '........', '..xxxx..', '........', 'S.......'],
  },
  {
    item: { kind: 'tool', id: 'stringTrimmer' },
    name: 'Trimmer Practice',
    hint: 'Tap TRIM beside the flowers: it cuts the grass all round, even over them.',
    rows: [
      '........',
      '.***....',
      '.*.*....',
      '.***....',
      '........',
      '....***.',
      '....*.*.',
      'S...***.',
    ],
  },
  {
    item: { kind: 'tool', id: 'sprinklerKey' },
    name: 'Sprinkler Practice',
    hint: 'Tap KEY near a sprinkler: it turns off, safe to mow over.',
    rows: ['........', '..q..q..', '........', '........', '..q..q..', '........', 'S.......'],
  },
  {
    item: { kind: 'tool', id: 'hedgeTrimmer' },
    name: 'Hedge Practice',
    hint: 'Tap HEDGE heading for a shrub: it trims it away.',
    rows: ['........', '.hhhhhh.', '........', '........', '.hhhhhh.', '........', 'S.......'],
  },
  {
    item: { kind: 'tool', id: 'shovel' },
    name: 'Shovel Practice',
    hint: 'Tap DIG heading for a rock: it digs it out.',
    rows: ['........', '..#..#..', '........', '...##...', '........', '..#..#..', 'S.......'],
  },
  {
    item: { kind: 'tool', id: 'pruningSaw' },
    name: 'Saw Practice',
    hint: 'Tap SAW heading for a tree: down it comes, leaving a stump.',
    rows: ['........', '.T....T.', '........', '...TT...', '........', '.T....T.', 'S.......'],
  },
  // ── Boosts ──
  {
    item: { kind: 'boost', id: 'shield' },
    name: 'Shield Practice',
    hint: 'Tap SHLD, then bump a rock: no heart lost while it lasts.',
    rows: ['........', '..#..#..', '........', '........', '..#..#..', '........', 'S.......'],
  },
  {
    item: { kind: 'boost', id: 'turbo' },
    name: 'Turbo Practice',
    hint: 'Tap TRBO for a burst of speed.',
    rows: [
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
      'S.......',
    ],
  },
  {
    item: { kind: 'boost', id: 'treat' },
    name: 'Treat Practice',
    hint: 'Tap TRT: the critters stop to munch and stay put.',
    rows: [
      '........',
      '........',
      '..h..h..',
      '........',
      '........',
      '..h..h..',
      '........',
      'S.......',
    ],
    critters: [
      { kind: 'squirrel', x: 1, y: 1 },
      { kind: 'squirrel', x: 6, y: 3 },
      { kind: 'squirrel', x: 3, y: 6 },
    ],
  },
  {
    item: { kind: 'boost', id: 'magnet' },
    name: 'Magnet Practice',
    hint: 'Tap MAG: coins near you fly to the mower.',
    rows: [
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
      '........',
      'S.......',
    ],
    coins: [
      [1, 1],
      [6, 1],
      [3, 3],
      [5, 4],
      [1, 5],
      [6, 6],
    ],
  },
  // ── Mowers with a trick ──
  {
    item: { kind: 'mower', id: 'reel' },
    mower: 'reel',
    name: 'Pull-Back Practice',
    hint: 'Swipe back the way you came to pull back out of a dead end.',
    rows: ['#.##.##.', '........', '........', '##.##.##', '........', 'S.......'],
  },
  {
    item: { kind: 'mower', id: 'selfPropelled' },
    mower: 'selfPropelled',
    name: 'Slow Gear Practice',
    hint: 'Tap SLOW in the top bar for a slow gear in tight spots; FAST again after.',
    rows: ['........', '.######.', '........', '######..', '........', '.######.', 'S.......'],
  },
  {
    item: { kind: 'mower', id: 'tractor' },
    mower: 'tractor',
    name: 'Tractor Practice',
    hint: 'It turns wide, but cuts tall grass in a single pass.',
    rows: [',,,,,,,,', ',,,,,,,,', '........', '........', ',,,,,,,,', ',,,,,,,,', 'S.......'],
  },
  {
    item: { kind: 'mower', id: 'zeroTurn' },
    mower: 'zeroTurn',
    name: 'Spin Practice',
    hint: 'At a dead end, hold a finger down: it stops and spins on the spot.',
    rows: ['.#.#.#.#', '.#.#.#.#', '.#.#.#.#', '........', 'S.......'],
  },
  {
    item: { kind: 'mower', id: 'fairway' },
    mower: 'fairway',
    name: 'Fairway Practice',
    hint: 'Its side reels mow three rows at once.',
    rows: [
      '.........',
      '.........',
      '.........',
      '.........',
      '.........',
      '.........',
      '.........',
      '.........',
      'S........',
    ],
  },
];

const itemKey = (item: PracticeItem) => `${item.kind}:${item.id}`;

export const PRACTICE_LAWNS: readonly PracticeLawn[] = SPECS.map((spec) => {
  const mower = spec.mower ?? 'reel';
  return {
    item: spec.item,
    mower,
    hint: spec.hint,
    level: loadLevel({
      id: `practice-${spec.item.id.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`,
      name: spec.name,
      startDir: 'N',
      mowerRule: { kind: 'only', mowers: [mower] },
      rows: spec.rows,
      ...(spec.critters ? { critters: spec.critters } : {}),
      ...(spec.coins ? { pickups: spec.coins.map(([x, y]) => ({ kind: 'coin', x, y })) } : {}),
    }),
  };
});

/** The TRY IT lawn for an item, if it has one. */
export function practiceFor(item: PracticeItem): PracticeLawn | undefined {
  return PRACTICE_LAWNS.find((p) => itemKey(p.item) === itemKey(item));
}
